import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/AddRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/EditRounded';
import { ModalConfirm } from '@components/Modal';
import ContactDrawer from '@features/ContactDrawer';
import Button from '@components/Button';
import { SearchInput } from '@components/SearchInput';
import { ImportIcon } from 'assets/images/icons';
import { useTranslation, useThrottleValue, trackExport } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { contactsActions, alertActions, channelsActions, labelsActions } from '@actions';
import { contactsService } from '@services';
import { ModalType } from '@constants/modal';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import { handleApiError } from '@helpers/api';
import { toDemographicsQuery } from '@helpers/contact-demographics';
import FiltersPanel from './FiltersPanel';
import ContactsTable from './ContactsTable';
import BulkUploadModal from './BulkUploadModal';
import BulkEditModal from './BulkEditModal';
import PageContainer from '@components/Layout/PageContainer';
import { FiltersWrapper, ToolbarWrapper, SearchWrapper } from './styles';

const ContactsPage = () => {
    const { t } = useTranslation();
    const firstRender = useRef(true);
    const isTablet = useMediaQuery('@media (max-width: 1024px)');
    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const { hasFullAccess } = usePermissions();
    const hasFullContacts = hasFullAccess('contacts');

    const [filter, setFilter] = useState({
        companies: [],
        timeframe: { start: null, end: null },
        contactLabels: [],
        searchQuery: '',
        sexes: [],
        languages: [],
        birthMonths: [],
        ageBracket: '',
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);
    const [pageRequests, setPageRequests] = useState(0);
    const [rowsPerPageRequests, setRowsPerPageRequests] = useState(DEFAULT_PAGE_SIZE);

    const [selectedContacts, setSelectedContacts] = useState({ list: {}, all: false });
    const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
    const [isBulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

    const language = useSelector(state => state.auth.user?.language);
    const {
        totalCount,
        contact,
        data: contacts,
        contactEditModal,
        locations: companies,
    } = useSelector(state => state.contacts);
    const { data: labels } = useSelector(state => state.labels);

    const dispatch = useDispatch();
    const throttledSearch = useThrottleValue(filter.searchQuery, 1000);

    useEffect(() => {
        dispatch(labelsActions.get());
        dispatch(channelsActions.get());
        dispatch(contactsActions.getLocations());
        return () => {
            dispatch(contactsActions.resetContacts());
        };
    }, [dispatch]);

    const currentQuery = useMemo(() => {
        // Selecting every location is equivalent to "no location filter": send an empty list so the
        // server skips the location predicate and includes unassigned contacts. A non-empty subset
        // filters to `Locations.Any(...)`, which (by design) excludes unassigned contacts.
        const allLocationsSelected = companies.length > 0 && filter.companies.length === companies.length;
        return {
            companies: allLocationsSelected ? [] : filter.companies.map(item => item.id),
            labels: filter.contactLabels,
            searchQuery: filter.searchQuery,
            from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'dd/MMM/yyyy') : null,
            to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'dd/MMM/yyyy') : null,
            demographics: toDemographicsQuery(filter),
        };
    }, [filter, companies]);

    useEffect(() => {
        if (firstRender.current) {
            dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));
            firstRender.current = false;
        }
    }, [pageRequests, currentQuery, rowsPerPageRequests, dispatch]);

    useEffect(() => {
        if (!firstRender.current) {
            dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));
        }
    }, [throttledSearch]);

    useEffect(() => {
        // INFO: refetch contacts if contact labels are edited
        if (contactEditModal.open) {
            dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));
        }
    }, [contactEditModal.open, labels]);

    const onPageChange = (_, newPage) => {
        setPageRequests(newPage);
        dispatch(contactsActions.get(currentQuery, newPage, rowsPerPageRequests));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPageRequests(parseInt(event.target.value, 10));
        setPageRequests(0);
        dispatch(contactsActions.get(currentQuery, 0, parseInt(event.target.value, 10)));
    };

    const onApplyFilters = () => {
        dispatch(contactsActions.get(currentQuery, 0, rowsPerPageRequests));
        setPageRequests(0);
    };

    const onClickContact = useCallback(
        (contactId, type) => {
            if (contactId !== contact?.id) {
                dispatch(contactsActions.getContact(contactId));
            }
            switch (type) {
                case 'delete':
                    dispatch(contactsActions.setContactDeleteModal(true));
                    break;
                case 'edit':
                    dispatch(contactsActions.setContactEditModal({ open: true, type: ModalType.Edit }));
                    break;
                case 'view':
                    dispatch(contactsActions.setContactDrawer(true));
                    break;
                default:
                    break;
            }
        },
        [contact?.id, dispatch],
    );

    const onExportContacts = async () => {
        const exportQuery = {
            ...currentQuery,
            contacts: Object.keys(selectedContacts.list),
            allContacts: selectedContacts.all,
        };
        try {
            const response = await contactsService.exportCSVBulk(exportQuery);
            if (response?.exportStatusId) {
                trackExport(response.exportStatusId);
                dispatch(
                    alertActions.success(t('exports.exporting'), { id: response.exportStatusId, persistent: true }),
                );
            }
        } catch (error) {
            handleApiError(error, 'common.exportError');
        }
        setSelectedContacts(prev => ({ ...prev, list: {}, all: false }));
    };

    const onDeleteContacts = async () => {
        const deleteQuery = {
            ...currentQuery,
            contacts: Object.keys(selectedContacts.list),
            allContacts: selectedContacts.all,
        };
        try {
            await contactsService.deleteBulkContacts(deleteQuery);
            await dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
        dispatch(contactsActions.setContactEditModal({ open: false, type: ModalType.Create }));
        setIsBulkDeleteModalOpen(false);
        setSelectedContacts(prev => ({ ...prev, list: {}, all: false }));
    };

    const onUpdateCallback = updatedContact => {
        if (contactEditModal.type === ModalType.Create) {
            dispatch(contactsActions.get(currentQuery, 0, rowsPerPageRequests));
        } else {
            const contactsCopy = contacts.slice();
            const updatedContactIndex = contactsCopy.findIndex(c => c.id === contact?.id);
            if (updatedContactIndex !== -1 && updatedContact) {
                contactsCopy[updatedContactIndex] = updatedContact;
                dispatch(contactsActions.setContacts(contactsCopy));
            }
        }
    };

    const onDeleteCallback = () => dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));

    const onMergeCallback = () => dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));

    const onOpenEditModal = () => dispatch(contactsActions.setContactEditModal({ open: true, type: ModalType.Create }));

    const selectedLength = Object.keys(selectedContacts.list).length;

    return (
        <PageContainer>
            <FiltersWrapper>
                <SearchWrapper>
                    <SearchInput
                        size="small"
                        value={filter.searchQuery}
                        onChange={searchQuery => setFilter(prev => ({ ...prev, searchQuery }))}
                        label={t(isTablet ? 'ContactsPage.search' : 'ContactsPage.searchLabel')}
                    />
                </SearchWrapper>
                <FiltersPanel
                    type="contacts"
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    onApplyFilters={onApplyFilters}
                    contactLabels={labels}
                    filter={filter}
                    setFilter={setFilter}
                    setFilterNumber={setFilterNumber}
                    withChips={false}
                />
                {!!selectedLength && hasFullContacts && (
                    <ToolbarWrapper>
                        <Button
                            sx={{ minWidth: 'min-content' }}
                            variant="text"
                            startIcon={<EditIcon />}
                            onClick={() => setIsBulkEditModalOpen(true)}
                        >
                            {!isMobile ? t('buttons.edit') : ''}
                        </Button>
                        <Button
                            sx={{ minWidth: 'min-content' }}
                            variant="text"
                            startIcon={<FileDownloadIcon sx={{ fontSize: '20px', transform: 'translateY(1px)' }} />}
                            onClick={onExportContacts}
                        >
                            {!isMobile ? t('buttons.export') : ''}
                        </Button>
                        <Button
                            sx={{ minWidth: 'min-content' }}
                            variant="text"
                            startIcon={<DeleteIcon />}
                            onClick={() => setIsBulkDeleteModalOpen(true)}
                        >
                            {!isMobile ? t('buttons.delete') : ''}
                        </Button>
                    </ToolbarWrapper>
                )}
                <div style={{ gridArea: 'controls', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    {!isMobile && hasFullContacts ? (
                        <Button
                            variant="outlined"
                            startIcon={<ImportIcon />}
                            onClick={() => setBulkUploadModalOpen(true)}
                        >
                            {t('buttons.import')}
                        </Button>
                    ) : null}
                    {hasFullContacts && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={onOpenEditModal}>
                            {t('buttons.create')}
                        </Button>
                    )}
                </div>
            </FiltersWrapper>
            <ContactsTable
                companies={companies}
                onClickContact={onClickContact}
                rowsPerPageRequests={rowsPerPageRequests}
                pageRequests={pageRequests}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                selectedContacts={selectedContacts}
                setSelectedContacts={setSelectedContacts}
                withCheckbox={hasFullContacts}
                canManage={hasFullContacts}
            />
            <ContactDrawer
                onDeleteCallback={onDeleteCallback}
                onMergeCallback={onMergeCallback}
                onUpdateCallback={onUpdateCallback}
            />
            {isBulkEditModalOpen ? (
                <BulkEditModal
                    modal={isBulkEditModalOpen}
                    setModal={setIsBulkEditModalOpen}
                    selectedContacts={selectedContacts}
                    currentQuery={currentQuery}
                    rowsPerPageRequests={rowsPerPageRequests}
                />
            ) : null}
            {isBulkUploadModalOpen ? (
                <BulkUploadModal
                    isOpen={isBulkUploadModalOpen}
                    setModal={setBulkUploadModalOpen}
                    locale={language}
                    onUpdateCallback={onUpdateCallback}
                />
            ) : null}
            {isBulkDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isBulkDeleteModalOpen}
                    handleClose={() => setIsBulkDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('ContactsPage.deleteContactsDescription', {
                        selectedContacts: selectedContacts.all ? totalCount : selectedLength,
                    })}
                    onPrimaryAction={onDeleteContacts}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </PageContainer>
    );
};

export default ContactsPage;
